import { PRICE_TYPES, normalizePriceType } from "../../subscription/priceType";
import { formatRevenue } from "../../../utils/revenueFormat";
import "../../../css/SubscriptionPriceTypeSummary.css";

const GRADES = [
  { key: "basic", label: "Basic", color: "#85B7EB" },
  { key: "standard", label: "Standard", color: "#378ADD" },
  { key: "premium", label: "Premium", color: "#185FA5" },
];

const emptyCells = () => Object.fromEntries(PRICE_TYPES.map((t) => [t.key, { count: 0, amount: 0 }]));
const sumOf = (cells, field) => PRICE_TYPES.reduce((sum, t) => sum + cells[t.key][field], 0);

/**
 * subscriptionDetail.{grade}.priceTypes 를 결제 유형(REGULAR · HALF · PROMO_100)별로 모으고 등급 합계를 낸다.
 * priceTypes 가 한 등급에도 없으면 hasData=false (서버 미제공 · 과거 월).
 */
export function summarizePriceTypes(subscriptionDetail) {
  const total = emptyCells();
  const byGrade = {};
  let hasData = false;

  GRADES.forEach(({ key }) => {
    const cells = emptyCells();
    const raw = subscriptionDetail?.[key]?.priceTypes;
    if (raw && typeof raw === "object") {
      Object.entries(raw).forEach(([type, value]) => {
        const typeKey = normalizePriceType(type);
        if (!typeKey || !value) return;
        hasData = true;
        const count = Number(value.count) || 0;
        const amount = Number(value.amount) || 0;
        cells[typeKey].count += count;
        cells[typeKey].amount += amount;
        total[typeKey].count += count;
        total[typeKey].amount += amount;
      });
    }
    byGrade[key] = cells;
  });

  return { hasData, byGrade, total, totalAmount: sumOf(total, "amount") };
}

/**
 * subscriptionDetail.{grade}.platforms 의 결제 경로 합계.
 * 앱 결제(IN_APP · 애플·구글)는 스토어 수수료를 뗀 실입금 netAmount 가 있으면 함께 모은다.
 */
export function summarizePlatforms(subscriptionDetail) {
  const nicepay = { count: 0, amount: 0 };
  const inApp = { count: 0, amount: 0, netAmount: 0 };
  let hasData = false;
  let hasNet = false;

  GRADES.forEach(({ key }) => {
    const raw = subscriptionDetail?.[key]?.platforms;
    if (!raw || typeof raw !== "object") return;
    Object.entries(raw).forEach(([platform, value]) => {
      const name = String(platform).toUpperCase().replace(/_/g, "");
      const target = name === "NICEPAY" ? nicepay : name === "INAPP" ? inApp : null;
      if (!target || !value) return;
      hasData = true;
      target.count += Number(value.count) || 0;
      target.amount += Number(value.amount) || 0;
      if (target === inApp && value.netAmount != null) {
        hasNet = true;
        inApp.netAmount += Number(value.netAmount) || 0;
      }
    });
  });

  return { hasData, hasNet, nicepay, inApp };
}

const countText = (count) => `${count.toLocaleString("ko-KR")}건`;

function SubscriptionPriceTypeSummary({ subscriptionDetail }) {
  const priceTypes = summarizePriceTypes(subscriptionDetail);
  const platforms = summarizePlatforms(subscriptionDetail);
  if (!priceTypes.hasData && !platforms.hasData) return null;

  const share = (amount) => {
    if (!(priceTypes.totalAmount > 0)) return "-";
    const pct = (amount / priceTypes.totalAmount) * 100;
    if (amount > 0 && pct < 0.1) return "0.1% 미만";
    return `${pct.toFixed(1)}%`;
  };

  return (
    <div className="chart-section sub-price-type">
      <h5>구독 결제 유형</h5>
      <p className="sub-price-type__hint">
        정상가로 결제한 금액이 실 구독 매출이에요. 50% 할인·100원 혜택 결제는 따로 모았어요.
      </p>

      {priceTypes.hasData && (
        <>
          <div className="sub-price-type__cards">
            {PRICE_TYPES.map(({ key, label }) => (
              <div key={key} className={`sub-price-type__card sub-price-type__card--${key.toLowerCase().replace("_", "-")}`}>
                <div className="sub-price-type__label">{key === "REGULAR" ? "실 구독 매출 (정상가)" : label}</div>
                <div className="sub-price-type__amount">{formatRevenue(priceTypes.total[key].amount)}</div>
                <div className="sub-price-type__meta">
                  {countText(priceTypes.total[key].count)} · 구독 결제의 {share(priceTypes.total[key].amount)}
                </div>
              </div>
            ))}
          </div>

          <div className="sub-price-type__table-scroll">
            <table className="sub-price-type__table">
              <thead>
                <tr>
                  <th>등급</th>
                  {PRICE_TYPES.map(({ key, label }) => (
                    <th key={key}>{label}</th>
                  ))}
                  <th>합계</th>
                </tr>
              </thead>
              <tbody>
                {GRADES.map(({ key, label, color }) => {
                  const cells = priceTypes.byGrade[key];
                  return (
                    <tr key={key}>
                      <td className="sub-price-type__grade">
                        <span className="sub-grade-dot" style={{ backgroundColor: color }} />
                        {label}
                      </td>
                      {PRICE_TYPES.map((t) => (
                        <td key={t.key}>
                          {formatRevenue(cells[t.key].amount)}
                          <span className="sub-price-type__count">{countText(cells[t.key].count)}</span>
                        </td>
                      ))}
                      <td className="sub-price-type__sum">
                        {formatRevenue(sumOf(cells, "amount"))}
                        <span className="sub-price-type__count">{countText(sumOf(cells, "count"))}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {platforms.hasData && (
        <div className="sub-price-type__platforms">
          <span className="sub-price-type__platforms-title">결제 경로</span>
          <span>
            나이스페이 <strong>{formatRevenue(platforms.nicepay.amount)}</strong> ({countText(platforms.nicepay.count)})
          </span>
          <span>
            앱 결제(애플·구글) <strong>{formatRevenue(platforms.inApp.amount)}</strong> ({countText(platforms.inApp.count)})
            {platforms.hasNet && (
              <>
                {" "}· 수수료 뺀 실입금 <strong>{formatRevenue(platforms.inApp.netAmount)}</strong>
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

export default SubscriptionPriceTypeSummary;
